import type { CanvasVariant, VariantAxis } from "./variant-resolver";
import { entryVariant, groupVariantsByRatio, sortGroupsByFit } from "./size-groups";

/**
 * The finish questions asked above the size list: what it is printed on, and
 * whether it comes stretched on a frame.
 *
 * Each is its own control rather than a caption under every size row. Folding
 * them into the size list made one 30 × 40 appear three times at three prices,
 * told apart only by small grey text — the customer had to read every row to
 * find the thing they wanted. Asked once, up front, the size list shrinks to
 * the sizes of the finish they chose.
 */
// Asked in this order. Surface comes last because it only has a choice for
// paper: every canvas row is matte, so for canvas it is never asked.
export const FINISH_AXES = ["material", "mount", "surface"] as const;
export type FinishAxis = (typeof FINISH_AXES)[number];
export type Finish = Partial<Record<FinishAxis, string>>;

const isActive = (v: CanvasVariant) => v.active !== false;

/** The finish a variant is, restricted to the finish axes it actually has. */
export function finishOf(variant: CanvasVariant | null | undefined): Finish {
  const out: Finish = {};
  for (const key of FINISH_AXES) {
    const value = variant?.axisValues?.[key];
    if (value != null) out[key] = value;
  }
  return out;
}

/** Active variants of exactly this finish. An empty finish matches all. */
export function variantsOfFinish(
  variants: CanvasVariant[],
  finish: Finish,
): CanvasVariant[] {
  const keys = Object.keys(finish) as FinishAxis[];
  return (variants ?? []).filter(
    (v) => isActive(v) && keys.every((k) => v.axisValues?.[k] === finish[k]),
  );
}

/**
 * The values of `key` that can be bought, given the finish axes that come
 * BEFORE it.
 *
 * Axes are asked in order — material, then mount — so mount's choices depend
 * on the material and not the other way round. That is what makes "with a
 * frame" disappear for paper: no active paper variant is stretched, so mount
 * has a single option and the question is not asked.
 *
 * Ordered as the definition declares the values, so the admin controls which
 * toggle comes first; values the definition does not declare trail behind.
 */
export function finishChoices(
  variants: CanvasVariant[],
  axes: VariantAxis[],
  finish: Finish,
  key: FinishAxis,
): string[] {
  const earlier = FINISH_AXES.slice(0, FINISH_AXES.indexOf(key));
  const found = new Set<string>();
  for (const v of variants ?? []) {
    if (!isActive(v)) continue;
    if (!earlier.every((k) => finish[k] == null || v.axisValues?.[k] === finish[k])) {
      continue;
    }
    const value = v.axisValues?.[key];
    if (value != null) found.add(value);
  }

  const declared = axes.find((a) => a.key === key)?.values.map((x) => x.value) ?? [];
  return [
    ...declared.filter((value) => found.has(value)),
    ...[...found].filter((value) => !declared.includes(value)),
  ];
}

/**
 * The finish after the customer changes one axis.
 *
 * Later axes keep their value when it is still sold and otherwise fall to the
 * first one that is: switching canvas → paper turns "stretched" into "rolled"
 * rather than leaving the customer on a combination that does not exist.
 */
export function changeFinish(
  variants: CanvasVariant[],
  axes: VariantAxis[],
  current: Finish,
  key: FinishAxis,
  value: string,
): Finish {
  const next: Finish = { ...current, [key]: value };
  for (const later of FINISH_AXES.slice(FINISH_AXES.indexOf(key) + 1)) {
    if (next[later] == null) continue;
    const choices = finishChoices(variants, axes, next, later);
    if (!choices.includes(next[later]!)) {
      if (choices.length) next[later] = choices[0];
      else delete next[later];
    }
  }
  return next;
}

/**
 * The variant among `candidates` nearest in size to `current`.
 *
 * Same dimensions win outright, so switching finish at 40 × 60 stays at
 * 40 × 60. Otherwise the same shape is preferred over a merely similar area —
 * the crop the customer made fits it without reshaping — and then the closest
 * area.
 */
export function closestBySize(
  candidates: CanvasVariant[],
  current: CanvasVariant | null | undefined,
): CanvasVariant | null {
  if (!candidates.length) return null;
  if (!current?.printSpec) return candidates[0];

  const dims = (v: CanvasVariant) => {
    const { widthMm = 0, heightMm = 0 } = v.printSpec ?? {};
    return { long: Math.max(widthMm, heightMm), short: Math.min(widthMm, heightMm) };
  };
  const target = dims(current);
  const targetRatio = target.short ? target.long / target.short : 1;
  const targetArea = target.long * target.short;

  const score = (v: CanvasVariant) => {
    const d = dims(v);
    const ratio = d.short ? d.long / d.short : 1;
    const shapeMiss = Math.abs(ratio - targetRatio) > 0.03 ? 1 : 0;
    const areaMiss = Math.abs(d.long * d.short - targetArea) / (targetArea || 1);
    return shapeMiss * 1000 + areaMiss;
  };

  return candidates.reduce((best, v) => (score(v) < score(best) ? v : best));
}

/**
 * What the page opens on, before the customer has touched anything.
 *
 * The advertised offer's finish (the entry enlargement the upload panel quoted
 * a price for), in the shape of the customer's own photo, at the size nearest
 * the advertised one. A 3:2 photo therefore lands exactly on the offer; a 4:3
 * photo lands on the 4:3 size closest to it rather than on a 3:2 that would
 * crop their picture. The shape is decided by the photo, so it never has to
 * be asked.
 *
 * `preferred` (from a link such as /canvas?material=canvas) overrides the
 * finish axes it names — "Na platnu" opens on stretched canvas, "Velike
 * fotografije" on paper — keeping the entry's other axes where that finish
 * has them. A finish nothing is sold in is ignored.
 */
export function startingVariant(
  variants: CanvasVariant[],
  currency: string,
  photoAspect: number,
  preferred?: Finish,
): CanvasVariant | null {
  const entry = entryVariant(variants, currency);
  if (!entry) return null;
  let sameFinish = variantsOfFinish(variants, finishOf(entry));
  if (preferred && Object.keys(preferred).length > 0) {
    const merged = variantsOfFinish(variants, { ...finishOf(entry), ...preferred });
    const loose = merged.length > 0 ? merged : variantsOfFinish(variants, preferred);
    if (loose.length > 0) sameFinish = loose;
  }
  const best = sortGroupsByFit(groupVariantsByRatio(sameFinish), photoAspect)[0];
  return closestBySize(best?.variants ?? [], entry) ?? entry;
}
